import { useLingui } from '@lingui/react/macro';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { z } from 'zod';

import type { ScreenManifest } from '@/shared/screen';
import { cn } from '@/shared/ui/cn';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

type ScreenTabs = NonNullable<ScreenManifest['place']['tabs']>;

/** Ids das abas do manifesto (U42), na ordem. */
export function tabIds(screen: Pick<ScreenManifest, 'place'>): string[] {
  return (screen.place.tabs ?? []).map((tab) => tab.id);
}

/**
 * `validateSearch` da aba: `?tab=` só aceita ids do `place.tabs`; valor desconhecido cai na
 * primeira (sem quebrar o link antigo). A primeira aba não vai para a URL.
 */
export function recordTabSearch(screen: Pick<ScreenManifest, 'place'>) {
  const ids = tabIds(screen);
  if (ids.length === 0) return z.object({});
  return z.object({ tab: z.enum(ids as [string, ...string[]]).optional().catch(undefined) });
}

export interface RecordTabsState {
  readonly tabs: ScreenTabs;
  readonly active: string;
  readonly select: (tab: string) => void;
}

/**
 * A aba ativa vem da URL (`?tab=`, U39 §2): F5 e link mantêm a aba, o voltar do navegador troca, e a
 * casca põe a aba no breadcrumb pelo mesmo `place.tabs`. `hidden` tira as abas condicionais
 * (permissão, modo novo); aba escondida na URL cai na primeira visível.
 */
export function useRecordTabs(screen: Pick<ScreenManifest, 'place'>, hidden: readonly string[] = []): RecordTabsState {
  const search: Record<string, unknown> = useSearch({ strict: false });
  const navigate = useNavigate();
  const tabs = (screen.place.tabs ?? []).filter((tab) => !hidden.includes(tab.id));
  const first = tabs[0]?.id ?? '';
  const requested = typeof search['tab'] === 'string' ? search['tab'] : undefined;
  const active = requested && tabs.some((tab) => tab.id === requested) ? requested : first;
  return {
    tabs,
    active,
    select: (tab) => {
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => {
          const { tab: _current, ...rest } = prev;
          return tab === first ? rest : { ...rest, tab };
        },
      });
    },
  };
}

export interface RecordTabsProps {
  readonly state: RecordTabsState;
  /** Contagem ao lado do rótulo ("Envolvidos 3"). */
  readonly counts?: Readonly<Record<string, number | undefined>>;
  /** Nome acessível da lista de abas. */
  readonly label?: string;
  /** Conteúdo da aba ativa; só ela é montada. */
  readonly children: (tab: string) => ReactNode;
  readonly className?: string;
}

/**
 * Abas do detalhe na mesma ordem do legado (U44), roteadas por `?tab=`. No iPhone a lista rola em X
 * (U12) com a aba ativa à vista.
 */
export function RecordTabs({ state, counts = {}, label, children, className }: RecordTabsProps) {
  const { i18n, t } = useLingui();
  if (state.tabs.length === 0) return <>{children(state.active)}</>;
  return (
    <Tabs value={state.active} onValueChange={state.select} className={cn('flex min-w-0 flex-col', className)}>
      <TabsList aria-label={label ?? t`Seções do registro`}>
        {state.tabs.map((tab) => {
          const count = counts[tab.id];
          return (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              ref={(node) => {
                if (node && tab.id === state.active) revealTab(node);
              }}
            >
              {i18n._(tab.label)}
              {count === undefined ? null : (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 font-mono text-xs text-muted-foreground tabular-nums">{count}</span>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
      <TabsContent value={state.active} className="flex flex-col gap-4">
        {children(state.active)}
      </TabsContent>
    </Tabs>
  );
}

// Traz a aba ativa para dentro da faixa, rolando só a faixa: `scrollIntoView` rolaria também a tela, e
// o detalhe abriria no meio numa janela baixa.
function revealTab(tab: HTMLElement) {
  const list = tab.parentElement;
  if (!list) return;
  const item = tab.getBoundingClientRect();
  const box = list.getBoundingClientRect();
  if (item.left < box.left) list.scrollLeft -= box.left - item.left;
  else if (item.right > box.right) list.scrollLeft += item.right - box.right;
}
